import React from 'react';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Divider, Flex, Form } from 'antd';

import { FieldStructure } from '@app/models/interfaces';

import { FieldGroupItemSchema } from '@app/models/orders';
import { Paper } from '@components/Paper';

import { useFieldGroupReorderQuery } from '../../../../../../hooks';

import { AddFieldModal } from './components/AddFieldModal';
import { DeleteGroup } from './components/DeleteGroup';
import { FieldRender } from './components/FieldRender';
import { GroupFieldName } from './components/GroupFieldName';

interface IProps {
  group: FieldGroupItemSchema;
}

export const CardRender: React.FC<IProps> = ({ group }) => {
  const [form] = Form.useForm();
  const [fields, setFields] = React.useState<FieldStructure[]>([]);
  const { mutate } = useFieldGroupReorderQuery();
  const { isDragging, setNodeRef, transform, transition } = useSortable({ id: group.data.uid });

  React.useEffect(() => {
    setFields(group.data.fields);
  }, [group]);

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? '0.6' : '1',
    zIndex: 100,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const newFieldIndex = group.data.fields.findIndex((item) => item.uid === over?.id);
    const groupUid = group.data.uid;

    if (over && active.id !== over?.id) {
      mutate({ field_group_uid: groupUid, data: { uid: active.id.toString(), position: newFieldIndex } });
      setFields((fields) => {
        const oldIndex = fields.findIndex((item) => item.uid === active.id);
        const newIndex = fields.findIndex((item) => item.uid === over.id);

        return arrayMove(fields, oldIndex, newIndex);
      });
    }
  };

  return (
    <div ref={setNodeRef}>
      <Paper key={group.data.uid} style={style}>
        <Flex justify="space-between">
          <GroupFieldName field_group_uid={group.data.uid} name={group.data.name} />
          <Flex gap={8}>{fields.length === 0 && <DeleteGroup field_group_uid={group.data.uid} />}</Flex>
        </Flex>
        <Divider />
        <Form form={form} name="basic1" labelCol={{ span: 6 }} wrapperCol={{ span: 15 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}>
            <Flex vertical gap={0}>
              <SortableContext items={fields.map((el) => el.uid) || []} strategy={verticalListSortingStrategy}>
                {fields.map((field) => (
                  <FieldRender form={form} field={field} key={field.uid} />
                ))}
              </SortableContext>
              <AddFieldModal field_group_uid={group.data.uid} />
            </Flex>
          </DndContext>
        </Form>
      </Paper>
    </div>
  );
};
